import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { makeObservable, observable, action, runInAction } from "mobx";
import { inject, injectable } from "inversify";
import { GetAllServicesUseCase } from "@catalogue/domain/usecases/get-all-services.usecase";
import { Service } from "@catalogue/domain/models/service.model";
import { Pagination } from "@core/domain/models/pagination";
import { GetLoggedUserUseCase } from "@authentication/domain/usecases/get-logged-user.usecase";
import { DeleteServiceUseCase } from "@catalogue/domain/usecases/delete-service.usecase";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";

@injectable()
export class ServiceListPageViewModel extends BaseViewModel {
    @observable services: Service[] = [];
    @observable loading: boolean = false;
    @observable filtersLoading: boolean = false;
    @observable filters: { name?: string; entityId?: number } = {};
    @observable pagination: Pagination = new Pagination();
    @observable totalServices: number = 0;

    constructor(
        @inject(GetAllServicesUseCase)
        private readonly getAllServicesUseCase: GetAllServicesUseCase,
        @inject(GetLoggedUserUseCase) // Inyecta el use case
        private readonly getLoggedUserUseCase: GetLoggedUserUseCase,
        @inject(DeleteServiceUseCase) // <-- Inyecta el DeleteServiceUseCase
        private readonly deleteServiceUseCase: DeleteServiceUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async fetchServices(): Promise<void> {
        this.loading = true;
        const user = this.getLoggedUserUseCase.execute(); // Obtiene el usuario logueado
        // Prepara los filtros, incluyendo siempre el entityId del usuario si no es SuperAdmin
        const requestFilters: { name?: string; entityId?: number } = { ...this.filters };

        if (user.entityId) {
            requestFilters.entityId = user.entityId;
        }

        console.log("Fetching services with filters:", {
            userId: user.id,
            userEntityId: user.entityId,
            isSuperAdmin: user.isSuperAdmin(),
            finalFilters: requestFilters
        });

        try {
            // Pasa los filtros modificados al use case
            const result = await this.getAllServicesUseCase.execute(requestFilters, this.pagination);
            runInAction(() => {
                this.services = result.items ?? []; // Asegura que sea un array
                this.totalServices = result.total ?? 0; // Asegura que sea un número
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @action
    async deleteService(serviceId: number): Promise<void> {
        LoadLayoutStore.start(); // Muestra indicador de carga
        try {
            const success = await this.deleteServiceUseCase.execute(serviceId);
            if (success) {
                // Refresca la lista si la eliminación fue exitosa
                await this.fetchServices();
            }
        } catch (error) {
            // El use case ya maneja el Toast de error, pero puedes loggear aquí si es necesario
            console.error("Error in ViewModel deleteService:", error);
        } finally {
            LoadLayoutStore.finish(); // Oculta indicador de carga
        }
    }

    @action
    setFilters(filters: { name?: string }): void {
        this.filters = filters;
        this.pagination.reset();
        this.fetchServices();
    }

    @action
    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;
        this.fetchServices();
    }

    override async didMount(): Promise<void> {
        await super.didMount();
        await this.fetchServices();
    }
}